import CachedIcon from "@mui/icons-material/Cached";
import ClearIcon from "@mui/icons-material/Clear";
import ListAltIcon from "@mui/icons-material/ListAlt";
import SearchIcon from "@mui/icons-material/Search";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import {
  Box,
  Container,
  Dialog,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Theme,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import Alert from "@mui/material/Alert";
import { DatePicker } from "@mui/x-date-pickers";
import ConfirmDialog from "components/ConfirmDialog";
import LazyTable, {
  DEFAULT_ROWS_PER_PAGE,
  TableCellType,
  TableSortType,
} from "components/LazyTable";
import OpenLinkInNewTabContextMenuItem from "components/OpenInNewTabContextMenuItem";
import SnackbarMessage from "components/SnackbarMessage";
import { format, isValid } from "date-fns";
import { FORM_ERROR } from "final-form";
import useGlobalContext from "hooks/globalData";
import useBoolean from "hooks/useBoolean";
import useDebounce from "hooks/useDebounce";
import useTitle from "hooks/useTitle";
import useTrashablePromises from "hooks/useTrashablePromises";
import { useEffect, useState } from "react";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQuery, useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";
import {
  ListRequestOfWebinarFilterExtended,
  ListResponseOfWebinarExtended,
  ProcessAttendanceRequest,
  ResponseCode,
  SaveResponse,
  WebinarExtended,
  WebinarFilterExtended,
} from "services/KnowfullyAdminClient";
import contextStore from "stores/contextStore";
import persistanceStore from "stores/persistanceStore";
import processAttendanceStore from "stores/processAttendanceStore";
import { SnackbarMessageProps } from "types/common-types";
import { isoDateFormat, standardDateFormat } from "utils/dateUtils";
import translateError from "utils/translateError";
import ProcessAttendanceButton from "./ProcessAttendanceButton";
import UpdateDeliveryKeyForm from "./UpdateDeliveryKeyForm";

const ID_SEPARATOR = "_";

const useStyles = (_: Theme) => ({
  tableTitleComponent: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
  },
});

const EventsList = () => {
  const styles = useStyles(useTheme());
  const { formatMessage: t } = useIntl();
  const navigate = useNavigate();
  const {
    webinarService,
    eventAttendanceRecordsUrl,
    skuDetailsUrl,
  } = useGlobalContext();
  const { organizationId } = contextStore.useState();

  const [order, setOrder] = useState<TableSortType>("asc");
  const [orderBy, setOrderBy] = useState("eventStartDate");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const searchTerm = persistanceStore.useState((s) => s.webinarSearchTerm);
  const trimmedSearchTerm = searchTerm.trim();
  const debouncedSearchTerm = useDebounce(trimmedSearchTerm, 500);
  const queryClient = useQueryClient();
  const { registerPromise } = useTrashablePromises();

  const [webinar, setWebinar] = useState<WebinarExtended | null>(null);
  const [
    deliveryMethodKeyEditOpen,
    deliveryMethodKeyEditOpenActions,
  ] = useBoolean(false);

  const [processAttendanceOpen, processAttendanceOpenActions] = useBoolean(
    false
  );

  const webinarStartDateSearch = persistanceStore.useState(
    (s) => s.webinarStartDateSearch
  );

  const webinarEndDateSearch = persistanceStore.useState(
    (s) => s.webinarEndDateSearch
  );

  const [
    snackbarMessage,
    setSnackbarMessage,
  ] = useState<SnackbarMessageProps | null>(null);

  useTitle("Webinars List");

  const {
    isError,
    data,
    error,
    isFetching,
    refetch,
  } = useQuery<ListResponseOfWebinarExtended>(
    [
      "webinarsList",
      debouncedSearchTerm,
      webinarStartDateSearch,
      webinarEndDateSearch,
      organizationId,
      page,
      rowsPerPage,
      orderBy,
      order,
    ],
    () => {
      const startDate =
        webinarStartDateSearch == null || !isValid(webinarStartDateSearch)
          ? new Date()
          : webinarStartDateSearch;
      const endDate =
        webinarEndDateSearch == null || !isValid(webinarEndDateSearch)
          ? new Date()
          : webinarEndDateSearch;

      return webinarService.getWebinars(
        ListRequestOfWebinarFilterExtended.fromJS({
          pageNumber: page,
          pageSize: rowsPerPage,
          sortField: orderBy,
          sortDescending: order === "desc",
          parameters: WebinarFilterExtended.fromJS({
            startDateString: format(startDate, isoDateFormat),
            endDateString: format(endDate, isoDateFormat),
            searchText: debouncedSearchTerm,
          }),
        })
      );
    }
  );

  useEffect(() => {
    setPage(0);
  }, [
    organizationId,
    debouncedSearchTerm,
    webinarStartDateSearch,
    webinarEndDateSearch,
  ]);

  if (isError) {
    return (
      <Alert severity="error">
        <pre>{JSON.stringify(error, null, 2)}</pre>
      </Alert>
    );
  }

  const clearProcessAttendanceDataBySku = (skuId?: string) => {
    processAttendanceStore.update((s) => {
      s.processAttendanceData = s.processAttendanceData.filter(
        (x) => x.skuId !== skuId
      );
    });
  };

  const processAttendance = async (value: WebinarExtended) => {
    try {
      processAttendanceStore.update((s) => {
        s.processAttendanceData.push({
          skuId: value.skuId!,
          organizationId: value.organizationId,
        });
      });

      const response: SaveResponse = await registerPromise(
        webinarService.processAttendance(
          new ProcessAttendanceRequest({
            skuId: value.skuId!,
            organizationId: value.organizationId,
          })
        )
      );

      if (response.responseCode === ResponseCode.OK) {
        processAttendanceOpenActions.setFalse();
        setSnackbarMessage(
          new SnackbarMessageProps(
            t(
              { id: "attendanceProcessedMessage" },
              { webinar: value.sku, organization: value.organizationName }
            )
          )
        );
      } else {
        clearProcessAttendanceDataBySku(value.skuId);
      }

      return response;
    } catch (err) {
      clearProcessAttendanceDataBySku(value.skuId);
      console.log(err);
      return { [FORM_ERROR]: translateError(err, t) };
    }
  };

  const webinars =
    data?.data?.map((w) => ({
      ...w,
      id: w.skuId + ID_SEPARATOR + w.organizationId,
    })) ?? [];

  const WebinarActions = ({ value }: { value: WebinarExtended }) => {
    const editDeliveryMethodAllowed =
      value.skuDeliverableId != null && !value.eventClosed;

    return (
      <Box justifyContent="center" display="flex">
        {!!value.attendanceRecordCount && (
          <Tooltip title={t({ id: "viewResults" })}>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                navigate(eventAttendanceRecordsUrl(value.skuId));
              }}
            >
              <ListAltIcon />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip
          title={
            value.skuDeliverableId != null
              ? t({ id: "editDeliveryMethodKeyLabel" })
              : t({ id: "notDeliveryMethodMessage" })
          }
        >
          <div>
            <IconButton
              size="small"
              disabled={!editDeliveryMethodAllowed}
              sx={{
                color: editDeliveryMethodAllowed ? "orange" : "lightgrey",
              }}
              onClick={(e) => {
                e.stopPropagation();
                setWebinar(value);
                deliveryMethodKeyEditOpenActions.setTrue();
              }}
            >
              <VpnKeyIcon />
            </IconButton>
          </div>
        </Tooltip>

        <ProcessAttendanceButton
          webinar={value}
          onClick={(e) => {
            e.stopPropagation();
            setWebinar(value);
            processAttendanceOpenActions.setTrue();
          }}
        />
      </Box>
    );
  };

  const onSubmitDeliveryMethodKey = async (values: any) => {
    try {
      const response = await registerPromise(
        webinarService.saveDeliveryMethodKey(
          webinar?.skuId!,
          values?.skuDeliveryMethodKey,
        )
      );

      queryClient.invalidateQueries(["webinarsList"]);
      queryClient.invalidateQueries(["webinar", webinar?.skuId!]);
      queryClient.invalidateQueries(["skuDeliveryMethods", webinar?.skuId!]);

      deliveryMethodKeyEditOpenActions.setFalse();

      return response;
    } catch (err: any) {
      return { [FORM_ERROR]: translateError(err, t) };
    }
  };

  const onUpdateStartDate = (date: Date) => {
    persistanceStore.update((s) => {
      if (isValid(date)) {
        s.webinarStartDateSearch = date;
      } else if (date == null) {
        s.webinarStartDateSearch = undefined;
      }
    });
  };

  const onUpdateEndDate = (date: Date) => {
    persistanceStore.update((s) => {
      if (isValid(date)) {
        s.webinarEndDateSearch = date;
      } else if (date == null) {
        s.webinarEndDateSearch = undefined;
      }
    });
  };

  const cells: Array<TableCellType> = [
    {
      accessor: "sku",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "skuLabel" }),
    },
    {
      accessor: "title",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "skuTitleLabel" }),
    },
    {
      accessor: "organizationName",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "organization" }),
      noSort: true,
    },
    {
      accessor: "eventStartDateTime",
      align: "left",
      width: 70,
      type: "text",
      customSortAccessor: "eventStartDate",
      label: t({ id: "startDateTimeLabel" }),
    },
    {
      accessor: "eventEndDateTime",
      align: "left",
      width: 70,
      type: "text",
      customSortAccessor: "eventEndDate",
      label: t({ id: "endDateTimeLabel" }),
    },
    {
      accessor: "skuDeliveryMethodKey",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "contentDeliveryKey" }),
    },
    {
      accessor: "registrationCount",
      align: "center",
      width: 0,
      type: "text",
      noSort: true,
      label: t({ id: "registrations" }),
    },
    {
      accessor: "value",
      align: "center",
      width: 0,
      type: "node",
      View: WebinarActions,
      noSort: true,
      label: t({ id: "actions" }),
    },
  ];

  const getSkuIdFromWebinarId = (webinarId: string) =>
    webinarId.substring(0, webinarId.indexOf(ID_SEPARATOR));

  const onClick = (id: string) => {
    navigate(skuDetailsUrl(getSkuIdFromWebinarId(id)), {
      state: { backToEvents: true },
    });
  };

  return (
    <Container>
      <Box m={3}>
        <Grid container spacing={2}>
          <Grid item sm={12} md={6}>
            <TextField
              fullWidth
              variant="outlined"
              value={searchTerm ?? ""}
              disabled={isFetching}
              placeholder={t({ id: "searchPlaceholder" })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() =>
                        persistanceStore.update((s) => {
                          s.webinarSearchTerm = "";
                        })
                      }
                    >
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              onChange={(e) =>
                persistanceStore.update((s) => {
                  s.webinarSearchTerm = e.target.value;
                })
              }
              data-cy="searchText"
            />
          </Grid>
          <Grid item sm={6} md={3}>
            <DatePicker
              label={t({ id: "startDateLabel" })}
              disabled={isFetching}
              value={webinarStartDateSearch}
              format={standardDateFormat}
              onChange={(date: any) => onUpdateStartDate(date)}
              data-cy="webinarStartDatePicker"
              slotProps={{
                textField: { fullWidth: true, variant: "standard" },
                actionBar: {
                  actions: ["clear", "cancel"],
                },
              }}
            />
          </Grid>
          <Grid item sm={6} md={3}>
            <DatePicker
              label={t({ id: "endDateLabel" })}
              disabled={isFetching}
              value={webinarEndDateSearch}
              format={standardDateFormat}
              onChange={(date: any) => onUpdateEndDate(date)}
              data-cy="webinarEndDatePicker"
              slotProps={{
                textField: { fullWidth: true, variant: "standard" },
                actionBar: {
                  actions: ["clear", "cancel"],
                },
              }}
            />
          </Grid>
        </Grid>
      </Box>
      <LazyTable
        {...{
          rowCount: data?.totalRecords ?? 0,
          rows: webinars,
          loading: isFetching,
          dense: true,
          titleComponent: (
            <Box sx={styles.tableTitleComponent}>
              <Box>
                <Typography variant="h6">
                  <T id="webinarsListLabel" />
                </Typography>
              </Box>
              <Tooltip
                title={t({
                  id: "refresh",
                })}
              >
                <IconButton onClick={() => refetch()}>
                  <CachedIcon />
                </IconButton>
              </Tooltip>
            </Box>
          ),
          cells,
          page,
          setPage,
          order,
          setOrder,
          orderBy,
          setOrderBy,
          rowsPerPage,
          setRowsPerPage,
          onClick,
          contextMenuItems: [
            OpenLinkInNewTabContextMenuItem((id) =>
              skuDetailsUrl(getSkuIdFromWebinarId(id))
            ),
          ],
        }}
      />
      <Dialog
        open={deliveryMethodKeyEditOpen}
        disableEnforceFocus
        maxWidth="sm"
        fullWidth
      >
        <UpdateDeliveryKeyForm
          entity={webinar}
          onCancel={deliveryMethodKeyEditOpenActions.setFalse}
          onSubmit={onSubmitDeliveryMethodKey}
        />
      </Dialog>
      <SnackbarMessage
        snackbarMessage={snackbarMessage}
        setSnackbarMessage={setSnackbarMessage}
      />
      <ConfirmDialog
        dialogTitleKey={t({ id: "processAttendanceLabel" })}
        contentKey={t({ id: "processAttendanceConfirmationMessage" })}
        open={processAttendanceOpen}
        onCancel={processAttendanceOpenActions.setFalse}
        handleConfirm={() => processAttendance(webinar!)}
        confirmLabel="executeButton"
      />
      <SnackbarMessage
        snackbarMessage={snackbarMessage}
        setSnackbarMessage={setSnackbarMessage}
      />
    </Container>
  );
};

export default EventsList;
